import React, { useState } from "react";
import s from "./APIManagement.module.scss";
import { ArrowDownIcon } from "../../../../global components/Icons/ArrowDownIcon";
import { ExportIcon } from "../../../../global components/Icons/ExportIcon";
import Card from "./local components/Card";
import CustomTable from "./local components/CustomTable";
import Keys from "./local components/Keys";
import PieCharts from "./local components/PieCharts/PieCharts";
import clsx from "clsx";
import SelectField from "../../../../global components/Forms/SelectField/SelectField";
import LineCharts from "./local components/LineCharts";
import { UseAuthToken } from "../../../../libs/api/userSession";
import useSWR from "swr";
import { swrFetcher } from "../../../../libs/api/Endpoints";
import {
  APILineChartType,
  APIStats,
  FrequentlyUsedEndpoints,
  TopActiveKeys,
  TopActiveUsersType,
} from "../../../../libs/Utils/Types/APIManagement";
import dayjs from "dayjs";
import { formatNumber } from "../../../../libs/common";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);
const PIE_COLORS = [
  "#6366F1",
  "#22C55E",
  "#F97316",
  "#EF4444",
  "#06B6D4",
  "#A855F7",
  "#EAB308",
  "#14B8A6",
  "#EC4899",
  "#64748B",
];
function APIManagement() {
  const token = UseAuthToken();
  const { data, isLoading } = useSWR<{ data: APIStats }, any, any>(
    token ? ["api/admin/api-keys/stats", token] : null,
    swrFetcher,
  );
  const { data: activeUsers, isLoading: loadingActiveUsers } = useSWR<
    { data: TopActiveUsersType[] },
    any,
    any
  >(token ? ["api/admin/api-keys/top-users", token] : null, swrFetcher);
  const { data: activeKeys, isLoading: loadingActiveKeys } = useSWR<
    { data: TopActiveKeys },
    any,
    any
  >(token ? ["api/admin/api-keys/top-keys", token] : null, swrFetcher);
  const [filter, setFilter] = useState<string>("1");

  const { data: frequentlyUsedEndpoints } = useSWR<
    { data: FrequentlyUsedEndpoints[] },
    any,
    any
  >(token ? ["api/admin/api-keys/endpoints", token] : null, swrFetcher);
  const { data: line } = useSWR<{ data: APILineChartType }, any, any>(
    token
      ? ["api/admin/api-keys/usage-trend", token, { durationInDays: filter }]
      : null,
    swrFetcher,
  );

  function handleFilter(value: string) {
    setFilter(value);
  }
  const trends: { request: number; date: string }[] =
    line?.data.chart.map((item) => ({
      date:
        filter === "1"
          ? dayjs(item.key).format("ha")
          : filter === "7"
            ? dayjs(item.key).format("ddd")
            : dayjs(item.key).format("MMM"),
      request: item.value,
    })) ?? [];
  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <div className={s.titles}>
          <h3 className={s.title}>API management</h3>
          <p className={s.subtitle}>
            Overview of system-wide API usage and performance
          </p>
        </div>
        <button className={s.exportBtn}>
          <ExportIcon className={s.icon} />
          Export <ArrowDownIcon className={s.icon} />
        </button>
      </div>
      <div className={s.cardWrapper}>
        <Card
          rate={`${data?.data.totalApiRequest.growthInPercentage}%`}
          stat={data?.data.totalApiRequest.value || 0}
          success={true}
          title="Total API Request"
          isLoading={isLoading}
          path={"/dashboard/api-management/requests"}
        />
        <Card
          rate={`${data?.data.successRate.growthInPercentage}%`}
          stat={data?.data.successRate.value || 0}
          success={true}
          title="Success Rate (2xx)"
          isLoading={isLoading}
          path={"/dashboard/api-management/requests"}
        />
        <Card
          rate={`${data?.data.clientError.growthInPercentage}%`}
          stat={data?.data.clientError.value || 0}
          success={false}
          title="Client Errors (4xx)"
          isLoading={isLoading}
          path={"/dashboard/api-management/requests"}
        />
        <Card
          rate={`${data?.data.serverError.growthInPercentage}%`}
          stat={data?.data.serverError.value || 0}
          success={true}
          title="Server Errors (5xx)"
          isLoading={isLoading}
          path={"/dashboard/api-management/requests"}
        />
      </div>
      <div className={s.charts}>
        <div className={s.lineChart}>
          <div className={s.head}>
            <h3 className={s.title}>API Usage Trends </h3>
            <SelectField
              bordered
              wrapperClassname={s.SelectWrapper}
              value={filter}
              defaultValue={"7"}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "1", label: "Last 24hrs" },
                { value: "7", label: "Last week" },
                { value: "365", label: "Last year" },
              ]}
            />
          </div>
          <span className={s.stat}>
            {line?.data.stats.totalVolume || 0} Request{" "}
            <span className={s.rate}>+{line?.data.stats.growthRate || 0}%</span>
          </span>
          <LineCharts data={trends} />
        </div>

        <div className={s.pieChart}>
          <div className={s.head}>
            <h3 className={s.title}>Most frequently used endpoints</h3>
          </div>
          <div className={s.body}>
            <PieCharts
              PIE_COLORS={PIE_COLORS}
              data={frequentlyUsedEndpoints?.data.map((item) => ({
                name: item.endpoint.replace("/api", ""),
                value: item.value,
              }))}
            />
            <div className={s.endpointWrapper}>
              {frequentlyUsedEndpoints?.data.map((item, index) => (
                <div
                  className={clsx(s.item, {
                    [s.immediate]:
                      item.endpoint.replace("/api/", "") === "message",
                    [s.schedule]:
                      item.endpoint.replace("/api/", "") === "messages",
                  })}
                  key={index}
                >
                  <span className={s.labelWrapper}>
                    {" "}
                    <span
                      className={s.percent}
                      style={{ backgroundColor: PIE_COLORS[index] }}
                    >
                      {item.percentage}%
                    </span>
                    <p className={s.label}>
                      {item.endpoint.replace("/api/", "")}
                    </p>
                  </span>

                  <p className={s.value}>{formatNumber(item.value)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className={s.customTableWrapper}>
        <CustomTable
          columns={["API Key", "Request Volume", "Status"]}
          tableTitle="Key Activity Ranking"
          data={
            activeKeys?.data.map((item) => ({
              colOne: item.keyPreview,
              colTwo: item.volume.toLocaleString(),
              colThree: item.status,
            })) ?? []
          }
          isLoading={loadingActiveKeys}
          path="/dashboard/api-management/requests/ranking"
        />
        <CustomTable
          columns={["User", "Request", "Last Activity"]}
          tableTitle="Most Active User"
          data={
            activeUsers?.data.map((item) => ({
              colOne: item.firstName + " " + item.lastName,
              colTwo: item.totalVolume.toLocaleString(),
              colThree: dayjs(item.lastUsedAt).fromNow(),
            })) ?? []
          }
          isLoading={loadingActiveUsers}
          path="/dashboard/api-management/requests/users"
        />
      </div>
      <Keys />
    </div>
  );
}

export default APIManagement;
